import { useTranslation } from 'react-i18next';
import { Badge } from './ui/badge';
import { Clock, AlertTriangle, CheckCircle2, PauseCircle } from 'lucide-react';
import { cn } from '../lib/utils';

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  
  const absMinutes = Math.abs(minutes);
  const sign = minutes < 0 ? '-' : '';
  
  if (absMinutes >= 1440) {
    const days = Math.floor(absMinutes / 1440);
    const hours = Math.floor((absMinutes % 1440) / 60);
    return hours > 0 ? `${sign}${days}d ${hours}h` : `${sign}${days}d`;
  }
  if (absMinutes >= 60) {
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return mins > 0 ? `${sign}${hours}h ${mins}m` : `${sign}${hours}h`;
  }
  return `${sign}${absMinutes}m`;
}

export function SLAWidget({ sla, compact = false, className }) {
  const { t } = useTranslation();
  
  if (!sla || !sla.sla_policy_id) {
    return null;
  }
  
  const {
    is_response_breached,
    is_resolution_breached,
    is_response_near_breach,
    is_resolution_near_breach,
    is_resolution_paused,
    response_remaining_minutes,
    resolution_remaining_minutes,
  } = sla;
  
  const hasBreaches = is_response_breached || is_resolution_breached;
  const hasNearBreaches = is_response_near_breach || is_resolution_near_breach;
  
  if (compact) {
    // Compact view for list pages - single line summary
    if (is_resolution_paused) {
      return (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
          <PauseCircle className="w-3 h-3 me-1" />
          {t('sla.paused')}
        </Badge>
      );
    }
    
    if (is_resolution_breached) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
          <AlertTriangle className="w-3 h-3 me-1" />
          {t('sla.resolutionBreached')}
        </Badge>
      );
    }
    
    if (is_response_breached) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
          <AlertTriangle className="w-3 h-3 me-1" />
          {t('sla.responseBreached')}
        </Badge>
      );
    }
    
    if (is_resolution_near_breach || is_response_near_breach) {
      return (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
          <Clock className="w-3 h-3 me-1" />
          {t('sla.nearBreach')}
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
        <CheckCircle2 className="w-3 h-3 me-1" />
        {t('sla.onTrack')}
      </Badge>
    );
  }
  
  // Full widget view for detail pages
  return (
    <div className={cn("space-y-2", className)}>
      {/* Response SLA */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {t('sla.responseDue')}:
        </span>
        <SLAStatusBadge
          remaining={response_remaining_minutes}
          isBreached={is_response_breached}
          isNearBreach={is_response_near_breach}
          isMet={response_remaining_minutes === 0 && !is_response_breached}
        />
      </div>
      
      {/* Resolution SLA */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t('sla.resolutionDue')}:
        </span>
        {is_resolution_paused ? (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
            <PauseCircle className="w-3 h-3 me-1" />
            {t('sla.paused')}
          </Badge>
        ) : (
          <SLAStatusBadge
            remaining={resolution_remaining_minutes}
            isBreached={is_resolution_breached}
            isNearBreach={is_resolution_near_breach}
            isMet={resolution_remaining_minutes === 0 && !is_resolution_breached}
          />
        )}
      </div>
    </div>
  );
}

function SLAStatusBadge({ remaining, isBreached, isNearBreach, isMet }) {
  const { t } = useTranslation();
  
  if (isMet) {
    return (
      <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
        <CheckCircle2 className="w-3 h-3 me-1" />
        {t('sla.onTrack')}
      </Badge>
    );
  }
  
  if (isBreached) {
    return (
      <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
        <AlertTriangle className="w-3 h-3 me-1" />
        {t('sla.overdueBy')} {formatDuration(Math.abs(remaining))}
      </Badge>
    );
  }
  
  if (isNearBreach) {
    return (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
        <Clock className="w-3 h-3 me-1" />
        {t('sla.dueIn')} {formatDuration(remaining)}
      </Badge>
    );
  }
  
  return (
    <span className="text-foreground">
      {t('sla.dueIn')} {formatDuration(remaining)}
    </span>
  );
}

export default SLAWidget;
